import { confluenceSessionStorageInstance as sessionStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';

import type { GraphQLContentStatus } from './ApprovalActivity/__generated__/ApprovalActivityHistoryQuery.graphql.ts';

export const APPROVAL_ACTIVITY_PANE_ID = 'approval-activity-pane';
export const APPROVAL_ACTIVITY_PANE_WIDTH = 400;
export const APPROVAL_ACTIVITY_PANE_CONTEXT_KEY = 'confluence.approvals.activityPane.contextKey';

export type ApprovalActivityPaneContext = {
	contentId: string;
	// Scoped to the GraphQL content-status union; today only the CURRENT cycle.
	contentStatus: GraphQLContentStatus;
};

type ApprovalActivityPaneOpenContext = ApprovalActivityPaneContext & {
	rightSidebarContext: {
		open: (panelId: string, width: number, isMinimized?: boolean, toTop?: boolean) => void;
	};
};

type ApprovalActivityPaneSyncContext = {
	isApprovalActivityEnabled: boolean;
	isSidebarApprovalActivityPaneOpen: boolean;
	contextKey: string;
	closeApprovalActivityPane: () => void;
};

type ShouldRenderApprovalActivityPaneArgs = {
	isApprovalActivityEnabled: boolean;
	isSidebarApprovalActivityPaneOpen: boolean;
	contextKey: string;
};

export const getApprovalActivityPaneContextKey = ({
	contentId,
	contentStatus,
}: ApprovalActivityPaneContext): string => `${contentId}:${contentStatus}`;

// Synchronous source of truth for the ownership helpers: session storage writes
// can be async (under `confluence_browser_storage_controls`), so an immediate
// read can miss. Storage is still written for durability across reloads.
let inMemoryContextKey: string | null = null;

export const getStoredApprovalActivityPaneContextKey = (): string | null => inMemoryContextKey;

export const setStoredApprovalActivityPaneContextKey = (contextKey: string): void => {
	inMemoryContextKey = contextKey;
	sessionStorageManager.setItem(APPROVAL_ACTIVITY_PANE_CONTEXT_KEY, contextKey);
};

export const clearStoredApprovalActivityPaneContextKey = (): void => {
	inMemoryContextKey = null;
	sessionStorageManager.removeItem(APPROVAL_ACTIVITY_PANE_CONTEXT_KEY);
};

export const isApprovalActivityPaneOwnedByContext = ({
	contextKey,
	storedContextKey,
}: {
	contextKey: string;
	storedContextKey: string | null;
}): boolean => storedContextKey === contextKey;

export const shouldRenderApprovalActivityPaneForContext = ({
	isApprovalActivityEnabled,
	isSidebarApprovalActivityPaneOpen,
	contextKey,
}: ShouldRenderApprovalActivityPaneArgs): boolean => {
	if (!isApprovalActivityEnabled || !isSidebarApprovalActivityPaneOpen) {
		return false;
	}

	return isApprovalActivityPaneOwnedByContext({
		contextKey,
		storedContextKey: getStoredApprovalActivityPaneContextKey(),
	});
};

export const syncApprovalActivityPaneContextOwnership = ({
	isApprovalActivityEnabled,
	isSidebarApprovalActivityPaneOpen,
	contextKey,
	closeApprovalActivityPane,
}: ApprovalActivityPaneSyncContext): void => {
	// When disabled while the pane is still open, close it so it can't linger
	// with stale content; always clear ownership so a re-enable can't match it.
	if (!isApprovalActivityEnabled) {
		if (isSidebarApprovalActivityPaneOpen) {
			closeApprovalActivityPane();
		}
		clearStoredApprovalActivityPaneContextKey();
		return;
	}

	if (!isSidebarApprovalActivityPaneOpen) {
		clearStoredApprovalActivityPaneContextKey();
		return;
	}

	const storedContextKey = getStoredApprovalActivityPaneContextKey();
	if (isApprovalActivityPaneOwnedByContext({ contextKey, storedContextKey })) {
		return;
	}

	closeApprovalActivityPane();
	clearStoredApprovalActivityPaneContextKey();
};

export const openApprovalActivityPaneForContext = ({
	contentId,
	contentStatus,
	rightSidebarContext,
}: ApprovalActivityPaneOpenContext): void => {
	// Open first so a failure can't leave an orphaned key that makes the
	// ownership helpers think a pane is open when it never actually opened.
	rightSidebarContext.open(APPROVAL_ACTIVITY_PANE_ID, APPROVAL_ACTIVITY_PANE_WIDTH);
	setStoredApprovalActivityPaneContextKey(
		getApprovalActivityPaneContextKey({ contentId, contentStatus }),
	);
};
